/**
 * QnA 컨텐츠를 네이버 광고 매칭용으로 전처리하는 유틸리티
 */

/**
 * HTML 태그를 제거하고 텍스트만 추출
 */
const stripHtmlTags = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

/**
 * 무의미한 내용들을 제거
 * - 과도한 줄바꿈
 * - 파일명 (확장자가 있는 문자열)
 * - URL 링크
 * - 특수문자 연속
 */
const removeNoiseContent = (text: string): string => {
  return (
    text
      // URL 제거 (http://, https://, www. 로 시작하는 것들)
      .replace(/https?:\/\/[^\s]+/g, '')
      .replace(/www\.[^\s]+/g, '')
      // 파일명 제거 (.확장자로 끝나는 문자열)
      .replace(/[^\s]+\.[a-zA-Z]{2,4}(?:\s|$)/g, '')
      // 연속된 줄바꿈을 하나로
      .replace(/\n{2,}/g, '\n')
      // 연속된 공백을 하나로
      .replace(/\s{2,}/g, ' ')
      // 특수문자 연속 제거
      .replace(/[^\w\s가-힣]{2,}/g, '')
      // 앞뒤 공백 제거
      .trim()
  );
};

/**
 * 문자열을 UTF-8 바이트 기준으로 자르기
 * 한글은 3바이트, 영문/숫자는 1바이트로 계산
 */
const truncateByBytes = (text: string, maxBytes: number): string => {
  let bytes = 0;
  let result = '';

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    // 한글 및 기타 유니코드 문자는 3바이트로 계산
    const charBytes = /[가-힣]/.test(char) ? 3 : 1;

    if (bytes + charBytes > maxBytes) {
      break;
    }

    bytes += charBytes;
    result += char;
  }

  return result.trim();
};

/**
 * QnA 컨텐츠를 네이버 광고 매칭용으로 전처리
 *
 * @param content - 원본 컨텐츠 (HTML 포함 가능)
 * @param maxBytes - 최대 바이트 수 (기본값: 1000)
 * @returns 전처리된 컨텐츠
 */
export const processContentForNaverAd = (content: string, maxBytes = 1000): string => {
  if (!content) {
    return '';
  }

  // 1. HTML 태그 제거
  let processed = stripHtmlTags(content);

  // 2. 무의미한 내용 제거
  processed = removeNoiseContent(processed);

  // 3. 바이트 제한에 맞춰 자르기
  processed = truncateByBytes(processed, maxBytes);

  return processed;
};

/**
 * 제목을 네이버 광고용으로 전처리
 *
 * @param title - 원본 제목
 * @param maxBytes - 최대 바이트 수 (기본값: 100)
 * @returns 전처리된 제목
 */
export const processTitleForNaverAd = (title: string, maxBytes = 100): string => {
  if (!title) {
    return '';
  }

  // 제목은 HTML 태그가 있을 가능성이 낮지만 안전하게 제거
  let processed = stripHtmlTags(title);

  // 앞뒤 공백 제거
  processed = processed.trim();

  // 바이트 제한에 맞춰 자르기
  processed = truncateByBytes(processed, maxBytes);

  return processed;
};
